import { apiClient } from "./apiClient";
import { DraftGeneratePayload, DraftPrompt, DraftReferenceDocument } from "../types";
import { getAuthContext } from "./authContext";

export const aiService = {
  async generateDocument(
    payload: DraftGeneratePayload
  ): Promise<{ document: string; raw: any; pendingHistoryId?: string; pendingMessage?: string }> {
    const auth = getAuthContext();
    const response = await apiClient.post(`/${auth.tenantId}/documents/generate`, payload);
    const raw = response.data;
    // Async (fire-and-forget) mode: backend queues generation and returns a historyId.
    const historyId = raw?.data?.historyId;
    if (historyId) {
      return {
        document: "",
        raw,
        pendingHistoryId: historyId,
        pendingMessage: raw?.message || "Document generation started. You will be notified when it completes.",
      };
    }
    // Sync mode: backend returns the generated HTML in `data.document`.
    const document =
      raw?.data?.document ||
      raw?.data?.content ||
      raw?.data?.generatedText ||
      raw?.content ||
      "";
    return { document, raw };
  },

  async getDocumentPrompts(documentType: string): Promise<DraftPrompt[]> {
    const auth = getAuthContext();
    const response = await apiClient.get(
      `/${auth.tenantId}/document-prompts?documentType=${encodeURIComponent(documentType)}`
    );
    const raw = response.data?.data ?? response.data;
    const list = Array.isArray(raw) ? raw : raw?.prompts || [];
    return list.map((item: any) => ({
      id: String(item.id),
      title: item.title || "Untitled Prompt",
      content: item.content || "",
      documentType: item.documentType || documentType,
    }));
  },

  async saveDocumentPrompt(payload: {
    title: string;
    content: string;
    documentType: string;
  }): Promise<any> {
    const auth = getAuthContext();
    const response = await apiClient.post(`/${auth.tenantId}/document-prompts`, payload);
    return response.data;
  },

  async deleteDocumentPrompt(promptId: string): Promise<any> {
    const auth = getAuthContext();
    const response = await apiClient.delete(`/${auth.tenantId}/document-prompts/${promptId}`);
    return response.data;
  },

  async getReferenceDocuments(
    searchText = "",
    page = 1,
    limit = 50
  ): Promise<DraftReferenceDocument[]> {
    const auth = getAuthContext();
    const query = searchText.trim()
      ? `page=${page}&limit=${limit}&searchText=${encodeURIComponent(searchText.trim())}`
      : `page=${page}&limit=${limit}`;
    const response = await apiClient.get(`/${auth.tenantId}/documents?${query}`);
    const docs = response.data?.data?.documents || response.data?.documents || [];
    return docs.map((doc: any) => ({
      id: String(doc.id),
      name: doc.name || doc.title || "Untitled Document",
      type: "document",
      caseTitle: doc.case?.title || "",
    }));
  },

  async checkGenerationStatus(
    historyId: string
  ): Promise<{ found: boolean; status?: string; document?: string; raw?: any }> {
    const auth = getAuthContext();
    const response = await apiClient.get(`/${auth.tenantId}/documents/generation-history`);
    const list = response.data?.data?.history || response.data?.history || [];
    const entry = list.find((h: any) => h?.id === historyId);
    if (!entry) {
      return { found: false };
    }
    // Backend stores the HTML draft at entry.result.document. Try a few alternative
    // field names too in case shape varies between document types.
    const document =
      entry?.result?.document ||
      entry?.result?.html ||
      entry?.result?.content ||
      entry?.document ||
      entry?.content ||
      "";
    return {
      found: true,
      status: entry?.status,
      document,
      raw: entry,
    };
  },
};
